/**
 * 发布窗口内的旧页面兜底。
 *
 * 前后端强类型契约（Zod `.strict()`）会在部署瞬间出现“旧前端 + 新后端”：
 * 后端响应新增字段时，仍停留在旧标签页的 bundle 会把整段 Zod 报错渲染到页面上。
 * 这里在查询失败时识别这类 schema 不匹配，自动刷新一次；同一标签页会话内再次
 * 出现时只提示，不再循环刷新（例如真正的后端契约缺陷）。
 */

const RELOAD_FLAG = 'youkuai:stale-client-reloaded';
export const STALE_CLIENT_EVENT = 'youkuai:stale-client';

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export type StaleClientOutcome = 'ignored' | 'reloaded' | 'notified';

const listeners = new Set<() => void>();

/** 订阅“需要刷新”提示；返回取消订阅函数。 */
export function subscribeStaleClient(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** 广播“需要刷新”，供页面顶部的提示条显示。 */
export function notifyStaleClient(): void {
  for (const listener of listeners) listener();
}

/** ZodError 及其 schema 校验失败（issues 里带 string code）都视为契约不匹配。 */
export function isSchemaMismatch(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const issues = (error as { issues?: unknown }).issues;
  if (!Array.isArray(issues) || issues.length === 0) return false;
  return issues.some((issue) => typeof issue === 'object' && issue !== null &&
    typeof (issue as { code?: unknown }).code === 'string');
}

function safeSessionStorage(): StorageLike | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function reloadPage(): void {
  if (typeof window !== 'undefined') window.location.reload();
}

export interface StaleClientDependencies {
  storage?: StorageLike | null;
  reload?: () => void;
  notify?: () => void;
}

/**
 * 处理一次查询失败：命中契约不匹配时，本会话第一次自动刷新，之后只提示。
 * 非契约错误（网络、403 等）原样忽略，交给页面自己的错误展示。
 */
export function handleStaleClientError(error: unknown, dependencies: StaleClientDependencies = {}): StaleClientOutcome {
  if (!isSchemaMismatch(error)) return 'ignored';

  const storage = dependencies.storage === undefined ? safeSessionStorage() : dependencies.storage;
  const notify = dependencies.notify ?? notifyStaleClient;
  // 存储不可用时无法保证只刷新一次，退化为提示，避免刷新循环。
  if (!storage) {
    notify();
    return 'notified';
  }

  if (storage.getItem(RELOAD_FLAG)) {
    notify();
    return 'notified';
  }

  storage.setItem(RELOAD_FLAG, new Date().toISOString());
  (dependencies.reload ?? reloadPage)();
  return 'reloaded';
}
